import React, { useState, useEffect } from 'react'
import { Table, Card, Input, Select, Space, Tag, Button, Tooltip, message, DatePicker, theme } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faEye,
  faDownload,
  faSync,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf
} from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { useLabOrders } from '../hooks/useLabOrders'
import { ORDER_STATUS, PRIORITY_LEVELS, PRIORITY_LEVEL_VALUES } from '../utils/constants'
import { getTests } from '../utils/utils'
import { hexToRGBA } from '../../lib/utils'

const { Search } = Input
const { Option } = Select
const { RangePicker } = DatePicker

const OrdersList = ({ onOrderSelect }) => {
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [dateRange, setDateRange] = useState([])
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [downloadIndex, setDownloadIndex] = useState(null)
  const [currentFilters, setCurrentFilters] = useState({})
  const [allTests, setAllTests] = useState([])

  const { orders, fetchOrders, refreshOrders, loading, resultsByOrderNumber, handleDownloadPDF } = useLabOrders()
  const { token } = theme.useToken()

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const filters = {
        searchText: searchText.trim() || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        priority: priorityFilter || undefined,
        dateRange: dateRange.length === 2 ? dateRange.map((date) => date.format('YYYY-MM-DD')) : undefined
      }

      // Remove undefined values
      const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== undefined))

      setCurrentFilters(cleanFilters)
      fetchOrders(cleanFilters)
    }, 500) // 500ms debounce

    return () => clearTimeout(timeoutId)
  }, [searchText, statusFilter, priorityFilter, dateRange, fetchOrders])

  useEffect(() => {
    fetchOrders()
    loadAllTests()
    // eslint-disable-next-line
  }, [])

  const loadAllTests = async () => {
    try {
      const tests = await getTests({}, 1, 10000)
      setAllTests(tests)
    } catch (error) {
      console.error('Failed to load tests:', error)
    }
  }

  const handleRefresh = async () => {
    await refreshOrders(currentFilters)
    message.success('Orders refreshed successfully')
  }

  const handleSearch = (value) => {
    setSearchText(value)
  }

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value)
  }

  const handlePriorityFilterChange = (value) => {
    setPriorityFilter(value)
  }

  const handleDateRangeChange = (dates) => {
    setDateRange(dates || [])
  }

  const clearFilters = () => {
    setSearchText('')
    setStatusFilter('all')
    setPriorityFilter('')
    setDateRange([])
  }

  const getStatusColor = (status) => {
    switch (status) {
      case ORDER_STATUS.ORDERED:
        return 'orange'
      case ORDER_STATUS.COMPLETE:
        return 'green'
      case ORDER_STATUS.EXPIRED:
        return 'red'
      default:
        return 'gray'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case ORDER_STATUS.ORDERED:
        return <FontAwesomeIcon icon={faHourglassHalf} style={{ color: getStatusColor(status) }} />
      case ORDER_STATUS.COMPLETE:
        return <FontAwesomeIcon icon={faCheckCircle} style={{ color: getStatusColor(status) }} />
      case ORDER_STATUS.EXPIRED:
        return <FontAwesomeIcon icon={faTimesCircle} style={{ color: getStatusColor(status) }} />
      default:
        return null
    }
  }

  const getPriorityText = (priority) => {
    switch (priority) {
      case PRIORITY_LEVEL_VALUES.STAT:
        return PRIORITY_LEVELS.STAT
      case PRIORITY_LEVEL_VALUES.ROUTINE:
        return PRIORITY_LEVELS.ROUTINE
      default:
        return priority
    }
  }

  const getTestName = (testCode) => {
    const test = allTests.find((t) => t.code === testCode)
    return test ? test.name : testCode
  }

  const columns = [
    {
      title: 'Order #',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 120,
      minWidth: 100,
      render: (text) => <strong>{text}</strong>,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl']
    },
    {
      title: 'Patient',
      dataIndex: 'patientRecord',
      key: 'patientRecord',
      width: 200,
      minWidth: 150,
      ellipsis: true,
      render: (_, record) => (
        <div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {record.patientRecord.firstName} {record.patientRecord.lastName}
          </div>
          <small style={{ color: '#8c8c8c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {record.patientRecord.idNo}
          </small>
        </div>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl']
    },
    {
      title: 'Test Name',
      dataIndex: 'orderData.orderedTests',
      key: 'orderedTests',
      width: 200,
      minWidth: 150,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
          <Tag>{getTestName(record.orderData.orderedTests[0])}</Tag>
          {record.orderData.orderedTests.length > 1 && (
            <Tooltip
              title={record.orderData.orderedTests
                .slice(1)
                .map((test) => getTestName(test))
                .join(', ')}
            >
              <Tag style={{ marginLeft: 4 }}>+{record.orderData.orderedTests.length - 1} more</Tag>
            </Tooltip>
          )}
        </div>
      ),
      responsive: ['sm', 'md', 'lg', 'xl', 'xxl']
    },
    {
      title: 'Ordered By',
      dataIndex: 'medicalProfRecord',
      key: 'medicalProfRecord',
      width: 150,
      minWidth: 120,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {record.medicalProfRecord.firstName} {record.medicalProfRecord.lastName}
        </div>
      ),
      responsive: ['md', 'lg', 'xl', 'xxl']
    },
    {
      title: 'Date Ordered',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      minWidth: 110,
      render: (date) => (date ? dayjs(date).format('DD-MM-YYYY') : '-'),
      sorter: (a, b) => {
        const dateA = a.createdAt ? dayjs(a.createdAt).unix() : 0
        const dateB = b.createdAt ? dayjs(b.createdAt).unix() : 0
        return dateA - dateB
      },
      responsive: ['sm', 'md', 'lg', 'xl', 'xxl']
    },
    {
      title: 'Priority',
      dataIndex: ['orderData', 'priority'],
      key: 'priority',
      width: 100,
      minWidth: 80,
      render: (priority, record) => {
        return <Tag color={priority === PRIORITY_LEVEL_VALUES.STAT ? 'red' : 'blue'}>{getPriorityText(priority)}</Tag>
      },
      responsive: ['md', 'lg', 'xl', 'xxl']
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      minWidth: 100,
      render: (status) => (
        <Space size={4}>
          {getStatusIcon(status)}
          <span>{status}</span>
        </Space>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl']
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      minWidth: 120,
      fixed: 'right',
      render: (_, record, index) => (
        <Space>
          <Button
            type='primary'
            size='small'
            icon={<FontAwesomeIcon icon={faEye} />}
            onClick={() => onOrderSelect(record)}
          >
            View
          </Button>
          {record.status === ORDER_STATUS.COMPLETE && (
            <Tooltip title='Download Results'>
              <Button
                size='small'
                icon={<FontAwesomeIcon icon={faDownload} />}
                onClick={async () => {
                  try {
                    setDownloadLoading(true)
                    setDownloadIndex(index)
                    const results = await resultsByOrderNumber(record.orderNumber, true)
                    const pdfs = results.pathologyReportPDFs

                    for (const pdf of pdfs) {
                      await handleDownloadPDF(pdf.pdf, record.orderNumber, pdf.messageId)
                    }
                  } catch (e) {
                    message.error('Failed to download results')
                  } finally {
                    setDownloadLoading(false)
                    setDownloadIndex(null)
                  }
                }}
                loading={downloadIndex === index && downloadLoading}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ]

  return (
    <Card>
      <div style={{ padding: 16, borderBottom: '1px solid #f0f0f0' }}>
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '16px'
            }}
          >
            <Space size='middle' wrap>
              <Search
                placeholder='Search by patient name, ID number, or order number'
                onSearch={handleSearch}
                onChange={(e) => setSearchText(e.target.value)}
                value={searchText}
                style={{ width: 'min(600px, 100%)', minWidth: 250 }}
                allowClear
                prefix={<FontAwesomeIcon icon={faSearch} />}
              />
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                style={{ width: 150, minWidth: 120 }}
                placeholder='Filter by status'
              >
                <Option value='all'>All Status</Option>
                <Option value={ORDER_STATUS.ORDERED}>Ordered</Option>
                <Option value={ORDER_STATUS.COMPLETE}>Complete</Option>
                <Option value={ORDER_STATUS.EXPIRED}>Expired</Option>
              </Select>

              <Select
                value={priorityFilter}
                onChange={handlePriorityFilterChange}
                style={{ width: 150, minWidth: 120 }}
                placeholder='Filter by priority'
              >
                <Option value=''>All Priorities</Option>
                <Option value={PRIORITY_LEVEL_VALUES.STAT}>STAT</Option>
                <Option value={PRIORITY_LEVEL_VALUES.ROUTINE}>ROUTINE</Option>
              </Select>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                style={{ width: 280, minWidth: 260 }}
                placeholder={['Start Date', 'End Date']}
                format='DD-MM-YYYY'
              />
              <Button
                onClick={clearFilters}
                disabled={!searchText && statusFilter === 'all' && !priorityFilter && dateRange.length === 0}
              >
                Clear Filters
              </Button>
            </Space>
            <Button
              onClick={handleRefresh}
              loading={loading}
              style={{
                background: hexToRGBA(token.colorPrimary, 0.1),
                borderColor: token.colorPrimary,
                color: token.colorPrimary,
                fontWeight: 'bold'
              }}
              icon={<FontAwesomeIcon icon={faSync} style={{ color: token.colorPrimary, fontSize: '16px' }} />}
            >
              Refresh
            </Button>
          </div>
        </Space>
      </div>

      <Table
        columns={columns}
        loading={loading}
        dataSource={orders}
        rowKey='id'
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} orders`,
          responsive: true,
          showQuickJumper: true
        }}
        scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
        style={{ width: '100%' }}
        size='middle'
        responsive={true}
      />
    </Card>
  )
}

export default OrdersList
